import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, getDoc, collection, query, where, getDocs, limit } from "firebase/firestore";
import { auth, db } from "../config/firebase";
import { signInWithProvider } from "../services/auth";
import { clearCache as clearAnalyticsCache } from "../services/analytics";
import { clearCrmCache } from "../services/crm";
import BootSpinner from "../components/BootSpinner";
import {
  readGodmodeClaims,
  readStoredTarget,
  sessionDeadline,
  recoverOrphanedSession,
  enterGodMode as beginGodModeSession,
  exitGodMode as endGodModeSession,
} from "../services/godmode";

const DASHBOARD_ROLES = ['admin', 'superadmin', 'org_owner', 'salesperson', 'affiliate', 'social_media'];

const unauthorized = (message) =>
  Object.assign(new Error(message), { code: 'app/unauthorized' });

/**
 * Undo an account the dashboard never wanted created.
 *
 * Firebase social sign-in always provisions an Auth user for an unrecognized
 * identity — there is no sign-in-only mode — so without this, anyone could
 * mint an account just by clicking "Sign in with Google" here. Deleting the
 * credential we just received keeps the dashboard signup-free; a plain sign-out
 * is the fallback if the delete is refused.
 */
const discardUnwantedAccount = async (user) => {
  try {
    await user.delete();
  } catch (err) {
    console.error('Could not remove unrecognized social account:', err);
    await firebaseSignOut(auth);
  }
};

/**
 * Gate a freshly authenticated user on dashboard access, signing them back out
 * if they don't have it. Shared by every sign-in path (password and social) so
 * a social account can never bypass the role check.
 */
const assertDashboardAccess = async (user) => {
  const userDoc = await getDoc(doc(db, "users", user.uid));
  const data = userDoc.data();
  const role = data?.role || (data?.isAdmin ? 'admin' : null);

  if (DASHBOARD_ROLES.includes(role)) return;

  // Check if user is an org manager
  let isManager = false;
  try {
    const managerCheck = await getDocs(
      query(collection(db, 'org_managers'), where('userId', '==', user.uid), limit(1))
    );
    isManager = !managerCheck.empty;
  } catch (_err) {
    // Permission denied means no org_managers access — not a manager
  }

  if (!isManager) {
    await firebaseSignOut(auth);
    throw unauthorized("Unauthorized: Admin or Organization Owner access required");
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userOrgId, setUserOrgId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [managerAssignments, setManagerAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  // True from the moment the signed-in identity changes until that identity's
  // role and memberships have loaded. The loader below sets them across two
  // awaits, so in between the app holds one user's role and another's
  // memberships. ProtectedRoute waits on this instead of judging that mix:
  // a plain manager (role 'user', access via org_managers) read as "no
  // access" in the gap and was bounced to /login — every god-mode session
  // into one, and a slow sign-in for the manager themselves.
  const [resolving, setResolving] = useState(false);

  /**
   * God mode (view as user).
   *
   * There is deliberately NO role or scope override here. The app signs its
   * one Firebase app into a token minted for the target, so the loader below
   * reads THEIR users doc and THEIR org_managers rows without knowing
   * anything about god mode. An override would be a second source of truth
   * for "who am I", and keeping two in agreement is the failure this feature
   * exists to detect.
   */
  const [godMode, setGodMode] = useState({
    active: false, target: null, actorUid: null, deadline: null,
  });
  const [godModeNotice, setGodModeNotice] = useState(null);
  const recoveryRan = useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Identity changed, so every cached figure belongs to someone else.
      //
      // analytics.js caches in localStorage with a 5-minute TTL, which
      // survives sign-out and page reloads. Its keys are scoped by booth id
      // and hold unscaled gross, so a user can only read an entry matching
      // their own scope — but nothing was clearing it on a sign-in, a
      // sign-out, or a god-mode session swap, and "probably safe because of
      // how the keys happen to be built" is not a property to rely on for
      // revenue figures on a shared machine.
      clearAnalyticsCache();
      clearCrmCache();

      setResolving(true);
      setCurrentUser(user);

      if (user) {
        // Before trusting this session, find out whether it is a god-mode one
        // the page reloaded into. A god-mode session whose restore token is
        // gone is signed out rather than continued — a superadmin must never
        // quietly carry on as someone else because a tab was reopened.
        if (!recoveryRan.current) {
          recoveryRan.current = true;
          const recovery = await recoverOrphanedSession();
          if (recovery.action === 'signed-out') {
            setGodModeNotice(
              recovery.reason === 'expired'
                ? 'Your god-mode session timed out. Please sign in again.'
                : 'A god-mode session was found with no way back to your own '
                  + 'account, so it was ended. Please sign in again.',
            );
            return; // the sign-out re-fires this listener
          }
          if (recovery.action === 'restored') {
            setGodModeNotice('Your god-mode session timed out and you are back on your own account.');
            return; // the restore re-fires this listener
          }
        }

        const claims = await readGodmodeClaims();
        setGodMode(claims
          ? {
            active: true,
            target: readStoredTarget(),
            actorUid: claims.actorUid,
            deadline: sessionDeadline(),
          }
          : { active: false, target: null, actorUid: null, deadline: null });

        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          const data = userDoc.data();
          setUserData(data);

          // Phase 5: `users.role` is now a denormalized flag computed from
          // org_managers by Cloud Functions. We still read it directly because
          // Firestore rules use it for "are you any org owner" checks, and
          // matching that contract here keeps role-gated UI consistent with
          // what the rules will actually allow.
          const role = data?.role || (data?.isAdmin ? 'admin' : null);
          setUserRole(role);

          const isPrivileged = role === 'admin' || role === 'superadmin' || data?.isAdmin === true;
          setIsAdmin(isPrivileged);

          // Load this user's org_managers rows. Phase 5 makes org_managers
          // the source of truth for which orgs a user belongs to, so we
          // always load it (not just for non-privileged users like before).
          // Superadmins still pass through OrganizationContext's full-org
          // query — these rows just become a no-op for them.
          try {
            const snap = await getDocs(query(
              collection(db, 'org_managers'),
              where('userId', '==', user.uid),
              limit(50)
            ));
            const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setManagerAssignments(rows);
            // Derive userOrgId for org_owners from the first owner row
            // (replaces the legacy users.organizationId field).
            if (role === 'org_owner') {
              const ownerRow = rows.find(r => r.role === 'owner') || rows[0];
              setUserOrgId(ownerRow?.orgId || null);
            } else {
              setUserOrgId(null);
            }
          } catch (err) {
            if (err?.code !== 'permission-denied') {
              console.error('Error loading manager assignments:', err);
            }
            setManagerAssignments([]);
            setUserOrgId(null);
          }
        } catch (error) {
          console.error("Error checking admin status:", error);
          setIsAdmin(false);
          setUserRole(null);
          setUserOrgId(null);
          setUserData(null);
          setManagerAssignments([]);
        }
      } else {
        setIsAdmin(false);
        setUserRole(null);
        setUserOrgId(null);
        setUserData(null);
        setManagerAssignments([]);
        setGodMode({ active: false, target: null, actorUid: null, deadline: null });
      }

      setResolving(false);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    await assertDashboardAccess(userCredential.user);
    return userCredential;
  };

  /**
   * Social sign-in ('facebook' | 'google' | 'apple'). Staff invited on the
   * customer site can create their account with a social provider, so the
   * dashboard has to accept the same providers or those accounts are locked
   * out. Returns null if the user closed the popup.
   */
  const signInWithSocial = async (providerId) => {
    const { user, cancelled, isNewUser } = await signInWithProvider(providerId);
    if (cancelled) return null;

    // The dashboard is invite-only. An identity Firebase has never seen can't
    // belong to invited staff, so drop the account it just created instead of
    // handing it to the role check.
    if (isNewUser) {
      await discardUnwantedAccount(user);
      throw unauthorized(
        "No dashboard account is registered to that login. Ask an administrator to invite you."
      );
    }

    await assertDashboardAccess(user);
    return user;
  };

  const signOut = () => {
    return firebaseSignOut(auth);
  };

  // ---- God mode actions -------------------------------------------------

  const enterGodMode = async (targetUid) => {
    setGodModeNotice(null);
    const target = await beginGodModeSession(targetUid);
    // No state to set: signing in re-fires the listener above, which reads
    // the claim and loads the target's own role and memberships.
    return target;
  };

  const exitGodMode = async (reason = 'exit') => {
    const result = await endGodModeSession(reason);
    if (!result.restored) {
      setGodModeNotice(
        'Could not get back to your own account automatically, so the session '
        + 'was ended. Please sign in again.',
      );
    }
    return result;
  };

  // Hard stop at the deadline. The token itself refreshes indefinitely, so
  // without this a view-as session would outlive the sitting that opened it.
  useEffect(() => {
    if (!godMode.active || !godMode.deadline) return undefined;
    const remaining = godMode.deadline - Date.now();
    if (remaining <= 0) { exitGodMode('expired'); return undefined; }
    const timer = setTimeout(() => { exitGodMode('expired'); }, remaining);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [godMode.active, godMode.deadline]);

  // Dev role override (development only)
  const [devRoleOverride, setDevRoleOverride] = useState(null);
  const isDev = import.meta.env.DEV;

  // The dev override is suppressed in god mode. It paints a different role
  // onto YOUR data, which is exactly the half-simulation god mode replaces —
  // layering it on top would show a salesperson's chrome over a different
  // user's numbers and look like a bug in whichever one you doubted.
  const overrideActive = isDev && devRoleOverride && !godMode.active;

  const effectiveUserRole = overrideActive ? devRoleOverride : userRole;
  const effectiveIsAdmin = overrideActive
    ? (devRoleOverride === 'admin' || devRoleOverride === 'superadmin')
    : isAdmin;
  const effectiveIsSalesperson = effectiveUserRole === 'salesperson';

  const value = {
    currentUser,
    isAdmin: effectiveIsAdmin,
    userRole: effectiveUserRole,
    userOrgId,
    userData,
    managerAssignments,
    isSalesperson: effectiveIsSalesperson,
    godMode,
    godModeNotice,
    dismissGodModeNotice: () => setGodModeNotice(null),
    enterGodMode,
    exitGodMode,
    signIn,
    signInWithSocial,
    signOut,
    loading,
    authResolving: resolving,
    // Dev only
    ...(isDev && !godMode.active
      ? { devRoleOverride, setDevRoleOverride, actualRole: userRole }
      : {}),
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <BootSpinner /> : children}
    </AuthContext.Provider>
  );
};
